/**
 * consumoDellAzienda — quanto ha consumato un'azienda di ogni strumento a canone nel periodo di un contatore, e cosa
 * le costa (atlante cap. 251). Le stesse righe per la pagina «Il mio abbonamento», per Yuma e per la console.
 *
 * I numeri vengono dai contatori scritti dal server (`tenants/{t}/consumi/{chiave}`), l'extra dalla stessa aritmetica
 * di Stripe (`extraInCentesimi`) con le condizioni che l'azienda paga davvero (`strumentoPerLAzienda`).
 *
 * Nucleo PURO: la lettura dei contatori la fa chi usa queste righe, col suo SDK.
 */

import { conConsumo, extraInCentesimi, strumentoPerLAzienda, type StrumentoACanone, type StrumentoConConsumo } from './listino';
import { aziendaPerGliStrumenti, haUnAbbonamentoVivo, viaDiAttivazione, type ViaDiAttivazione } from './attivazione';

export interface ContatoreDelPeriodo {
  readonly strumenti: Readonly<Record<string, { quantita?: number; eventi?: number }>>;
}

/** Una riga per ogni strumento acceso che CONTA un consumo: quelli solo a canone non hanno niente da contare. */
export interface RigaDiConsumo {
  readonly strumento: StrumentoConConsumo;
  readonly usate: number;
  readonly quota: number;
  /** Unità oltre la quota (0 se dentro). */
  readonly oltre: number;
  /** L'extra maturato finora, in centesimi, come lo calcola Stripe. */
  readonly extra_centesimi: number;
  /** Come lo si paga per questa azienda (gratis, in prova, nell'abbonamento…). */
  readonly via: ViaDiAttivazione;
  /** Acceso come regalo della piattaforma (`modules.override`): non è nell'abbonamento, non si addebita. */
  readonly regalato: boolean;
}

function lista(datiAzienda: unknown, chiave: string): string[] {
  const modules = ((datiAzienda as { modules?: unknown } | null | undefined)?.modules ?? {}) as Record<string, unknown>;
  return Array.isArray(modules[chiave]) ? (modules[chiave] as string[]) : [];
}

/** Gli strumenti a canone accesi per l'azienda (da qualunque strada: pagati, in prova, regalati). */
export function strumentiAccesi(datiAzienda: unknown, listino: ReadonlyMap<string, StrumentoACanone>): StrumentoACanone[] {
  const scaduti = new Set(lista(datiAzienda, 'expired'));
  const accesi = new Set([...lista(datiAzienda, 'base'), ...lista(datiAzienda, 'addon'), ...lista(datiAzienda, 'override')].filter((c) => !scaduti.has(c)));
  return [...listino.values()].filter((s) => accesi.has(s.capability));
}

/** Pura: le righe del consumo di un'azienda, dato il contatore del periodo (null = niente consumato). */
export function righeDiConsumo(
  datiAzienda: unknown,
  listino: ReadonlyMap<string, StrumentoACanone>,
  contatore: ContatoreDelPeriodo | null,
  oraMs: number,
): RigaDiConsumo[] {
  const via = viaDiAttivazione(aziendaPerGliStrumenti(datiAzienda), oraMs);
  const regali = new Set(lista(datiAzienda, 'override'));
  const pagati = new Set(lista(datiAzienda, 'addon'));
  // Le condizioni che l'azienda paga davvero: una versione di prezzo precedente, se ce l'ha nell'abbonamento.
  return strumentiAccesi(datiAzienda, listino).map((l) => strumentoPerLAzienda(datiAzienda, l)).filter(conConsumo).map((s) => {
    const usate = Math.max(0, Math.floor(contatore?.strumenti?.[s.capability]?.quantita ?? 0));
    const quota = s.consumo.quota_compresa;
    return {
      strumento: s, usate, quota, oltre: Math.max(0, usate - quota), extra_centesimi: extraInCentesimi(s.consumo, usate), via,
      regalato: regali.has(s.capability) && !pagati.has(s.capability),
    };
  });
}

/** L'extra di questa riga arriva davvero in fattura? Solo nell'abbonamento, fuori prova, e non per un regalo. */
export function extraInFattura(r: RigaDiConsumo): boolean {
  return !r.regalato && r.via.via === 'nell_abbonamento' && r.via.gratis_fino_al_ms === null;
}

/**
 * Il consumo di UN PERIODO dei contatori arriva in fattura? (02/11/2026)
 *
 * `extraInFattura` guarda la situazione di oggi e vale per il periodo in corso. I periodi passati vanno guardati per
 * quello che erano: la console scriveva «in fattura 6,23 €» per le simulazioni di Fortezza di ottobre, che Stripe non ha
 * addebitato perché l'azienda era in prova fino al 31/10. Un periodo cominciato prima della fine della prova è gratis
 * (i periodi dell'abbonamento partono dalla fine della prova: `ancoraFatturazione`). Esenti, dimostrative, regali e
 * aziende senza abbonamento non pagano. PURA.
 */
export type ConsumoDelPeriodo = 'in_fattura' | 'in_prova' | 'non_si_paga';

export function consumoDelPeriodo(
  datiAzienda: unknown,
  r: Pick<RigaDiConsumo, 'regalato'>,
  periodo: { readonly inizio_ms: number },
): ConsumoDelPeriodo {
  const azienda = aziendaPerGliStrumenti(datiAzienda);
  if (azienda.gratuita || r.regalato) return 'non_si_paga';
  if (azienda.fine_prova_ms !== null && periodo.inizio_ms < azienda.fine_prova_ms) return 'in_prova';
  return haUnAbbonamentoVivo(azienda) ? 'in_fattura' : 'non_si_paga';
}

/** Il contatore dai dati del documento (null se il documento non c'è). */
export function contatoreDaDati(dati: Record<string, unknown> | undefined): ContatoreDelPeriodo | null {
  if (!dati) return null;
  const s = dati.strumenti;
  return { strumenti: (s && typeof s === 'object' ? s : {}) as ContatoreDelPeriodo['strumenti'] };
}
